export interface OverlapLaneLayout<T> {
  item: T;
  laneIndex: number;
  laneCount: number;
  clusterIndex: number;
}

interface NormalizedOverlapEntry<T> {
  item: T;
  id: string;
  start: number;
  end: number;
}

interface BuildOverlapLaneLayoutOptions<T> {
  getId: (item: T) => string;
  getStart: (item: T) => number;
  getEnd: (item: T) => number;
}

function normalizeOverlapBoundary(value: number, fallback: number): number {
  return Number.isFinite(value) ? value : fallback;
}

export function buildOverlapLaneLayout<T>(
  items: readonly T[],
  options: BuildOverlapLaneLayoutOptions<T>,
): OverlapLaneLayout<T>[] {
  if (items.length === 0) {
    return [];
  }

  const normalizedEntries: NormalizedOverlapEntry<T>[] = items
    .map((item) => {
      const id = options.getId(item);
      const start = normalizeOverlapBoundary(options.getStart(item), 0);
      const rawEnd = normalizeOverlapBoundary(options.getEnd(item), start);
      const end = rawEnd > start ? rawEnd : start + 0.0001;

      return {
        item,
        id,
        start,
        end,
      };
    })
    .sort((left, right) => {
      if (left.start !== right.start) {
        return left.start - right.start;
      }

      if (left.end !== right.end) {
        return left.end - right.end;
      }

      return left.id.localeCompare(right.id);
    });

  const layoutsById = new Map<string, OverlapLaneLayout<T>>();
  let currentCluster: NormalizedOverlapEntry<T>[] = [];
  let currentClusterMaxEnd = Number.NEGATIVE_INFINITY;
  let clusterIndex = 0;

  const flushCluster = (entries: NormalizedOverlapEntry<T>[], currentClusterIndex: number) => {
    if (entries.length === 0) {
      return;
    }

    const laneEndTimes: number[] = [];
    const laneIndicesById = new Map<string, number>();
    let clusterLaneCount = 1;

    entries.forEach((entry) => {
      let laneIndex = laneEndTimes.findIndex((laneEnd) => laneEnd <= entry.start);
      if (laneIndex === -1) {
        laneIndex = laneEndTimes.length;
        laneEndTimes.push(entry.end);
      } else {
        laneEndTimes[laneIndex] = entry.end;
      }

      clusterLaneCount = Math.max(clusterLaneCount, laneEndTimes.length);
      laneIndicesById.set(entry.id, laneIndex);
    });

    entries.forEach((entry) => {
      layoutsById.set(entry.id, {
        item: entry.item,
        laneIndex: laneIndicesById.get(entry.id) ?? 0,
        laneCount: clusterLaneCount,
        clusterIndex: currentClusterIndex,
      });
    });
  };

  normalizedEntries.forEach((entry) => {
    if (currentCluster.length === 0) {
      currentCluster = [entry];
      currentClusterMaxEnd = entry.end;
      return;
    }

    if (entry.start >= currentClusterMaxEnd) {
      flushCluster(currentCluster, clusterIndex);
      clusterIndex += 1;
      currentCluster = [entry];
      currentClusterMaxEnd = entry.end;
      return;
    }

    currentCluster.push(entry);
    currentClusterMaxEnd = Math.max(currentClusterMaxEnd, entry.end);
  });

  flushCluster(currentCluster, clusterIndex);

  return items.map((item) => {
    const id = options.getId(item);
    return layoutsById.get(id) ?? {
      item,
      laneIndex: 0,
      laneCount: 1,
      clusterIndex: 0,
    };
  });
}
